import {
  addMonths,
  endOfMonth,
  isAfter,
  max,
  min,
  startOfMonth,
} from 'date-fns';

import { DateFormat, formatDate, parseDate } from '@/utils/dateUtils';

export interface MonthFetchRange {
  startDate: string;
  endDate: string;
}

interface NextAvailableBounds {
  /** Only consider dates strictly after this date. */
  after: Date;
  /** Only consider dates on or before this date (the bookable window end). */
  maxDate: Date;
}

/**
 * Finds the EARLIEST date in an availability map that has availability and falls
 * within `(after, maxDate]`. Returns `null` when none qualifies.
 *
 * Availability can be fetched past the bookable window (the display range is
 * wider), so callers pass `maxDate = bookingWindowEnd` to avoid selecting a day
 * the calendar would disable. Picking the earliest (rather than the first by map
 * order) keeps "next available" deterministic across the booking and reschedule
 * flows.
 */
export function findNextAvailableDate(
  availabilityMap: Map<string, number>,
  { after, maxDate }: NextAvailableBounds,
): Date | null {
  let earliest: Date | null = null;

  availabilityMap.forEach((availability, dateKey) => {
    if (availability <= 0) return;
    const date = parseDate(dateKey);
    if (!isAfter(date, after) || date > maxDate) return;
    if (!earliest || date < earliest) earliest = date;
  });

  return earliest;
}

/**
 * Returns the API date range (`yyyy-MM-dd`) to fetch availability for a single
 * calendar month, clamped to the overall booking window `[rangeStart, rangeEnd]`.
 *
 * - The first in-window month is clamped up to `rangeStart` (we never fetch
 *   before today).
 * - The last in-window month is clamped down to `rangeEnd` (we never fetch past
 *   the barber's advance-booking window).
 * - Returns `null` when the month lies entirely outside the window, so callers
 *   can skip issuing a query for it.
 */
export function getMonthFetchRange(
  month: Date,
  rangeStart: Date,
  rangeEnd: Date,
): MonthFetchRange | null {
  const clampedStart = max([startOfMonth(month), rangeStart]);
  const clampedEnd = min([endOfMonth(month), rangeEnd]);

  // Empty interval => month is fully outside the window.
  if (isAfter(clampedStart, clampedEnd)) return null;

  return {
    startDate: formatDate(clampedStart, DateFormat.API_DATE),
    endDate: formatDate(clampedEnd, DateFormat.API_DATE),
  };
}

/**
 * Returns a single API date range (`yyyy-MM-dd`) covering the given month plus
 * the next one, clamped to the booking window `[rangeStart, rangeEnd]`. Used by
 * the flows that fetch a rolling 2-month span (group bookings, reschedule,
 * waitlist) so a large advance-book window isn't loaded up front.
 *
 * Falls back to the window bounds when a month sits outside the window, so the
 * result is always a usable in-window span.
 */
export function getCurrentAndNextMonthRange(
  month: Date,
  rangeStart: Date,
  rangeEnd: Date,
): MonthFetchRange {
  const currentRange = getMonthFetchRange(month, rangeStart, rangeEnd);
  const nextRange = getMonthFetchRange(
    startOfMonth(addMonths(month, 1)),
    rangeStart,
    rangeEnd,
  );

  return {
    startDate:
      currentRange?.startDate ?? formatDate(rangeStart, DateFormat.API_DATE),
    endDate:
      nextRange?.endDate ??
      currentRange?.endDate ??
      formatDate(rangeEnd, DateFormat.API_DATE),
  };
}
